import { json2csv } from "json-2-csv";
import { SortingState } from "@tanstack/react-table";
import { ConfigurableTableSystem, TableColumnConfig } from "./TableConfigurationSystem";

export interface ExportDataParams<TData, TColumn extends string> {
  exportType?: "csv";
  columnSettings: Record<string, boolean>;
  columnOrder?: TColumn[];
  data: TData[];
  mapDataToExport: (item: TData, columns: TColumn[]) => Record<string, unknown>;
  filename?: string;
}

/**
 * Generic table export utility that handles column ordering and sorting
 */
export async function exportTableData<TData, TColumn extends string>({
  exportType = "csv",
  columnSettings,
  columnOrder,
  data,
  mapDataToExport,
  filename,
}: ExportDataParams<TData, TColumn>): Promise<void> {
  if (!data || data.length === 0) {
    throw new Error("No data found to export");
  }

  // Get enabled columns
  const enabledColumns = Object.entries(columnSettings)
    .filter(([, value]) => value)
    .map(([key]) => key as TColumn);

  // Sort columns according to user's preferred order if available
  let selectedColumns: TColumn[];
  if (columnOrder && columnOrder.length > 0) {
    // Use the user's column order, but only include enabled columns
    selectedColumns = columnOrder.filter((col) =>
      enabledColumns.includes(col),
    );

    // Add any enabled columns that aren't in the order (safety check)
    const missingColumns = enabledColumns.filter(
      (col) => !selectedColumns.includes(col),
    );
    selectedColumns.push(...missingColumns);
  } else {
    // Fall back to enabled columns in their default order
    selectedColumns = enabledColumns;
  }

  // Map data to export format
  const exportData = data.map((item) => mapDataToExport(item, selectedColumns));

  if (!exportData || exportData.length === 0) {
    throw new Error("No data to export after processing");
  }

  if (exportType === "csv") {
    const csv = json2csv(exportData);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `export-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

/**
 * Build query parameters for fetching sorted data
 */
export function buildSortedQueryParams<TData, TColumn extends string>(
  sorting: SortingState | undefined,
  tableColumns: Record<TColumn, TableColumnConfig<TData, TColumn>> | undefined,
): { orderBy?: string } {
  if (!sorting || !tableColumns) {
    return {};
  }

  const orderBy = ConfigurableTableSystem.generateSortParameter(
    tableColumns,
    sorting,
  );

  return orderBy ? { orderBy } : {};
}

/**
 * Batch fetch utility for large datasets
 */
export async function batchFetchData<TQuery, TResult>(
  fetchFn: (offset: number) => Promise<TResult>,
  extractData: (result: TResult) => TQuery[],
  batchSize: number = 100,
): Promise<TQuery[]> {
  const allData: TQuery[] = [];
  let hasNextPage = true;
  let offset = 0;

  while (hasNextPage) {
    const result = await fetchFn(offset);
    const batch = extractData(result);
    allData.push(...batch);

    // Check if we have more data to fetch
    hasNextPage = batch.length === batchSize;
    offset += batchSize;
  }

  return allData;
}